import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Notification } from '../../lib/http';

interface NotificationCardProps {
  notification: Notification;
  onMarkRead?: (id: string) => void;
}

const TYPE_CONFIG: Record<string, { color: string; bg: string; icon: React.ReactNode }> = {
  'booking.reminder': {
    color: 'text-blue-600',
    bg: 'bg-blue-100',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  'booking.confirmed': {
    color: 'text-green-600',
    bg: 'bg-green-100',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  'booking.cancelled': {
    color: 'text-red-600',
    bg: 'bg-red-100',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  'policy.hint': {
    color: 'text-amber-600',
    bg: 'bg-amber-100',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
};

const DEFAULT_CONFIG = {
  color: 'text-gray-500',
  bg: 'bg-gray-100',
  icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  ),
};

function getRelativeTime(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  onMarkRead,
}) => {
  const navigate = useNavigate();
  const isUnread = !notification.read;
  const config = TYPE_CONFIG[notification.type] || DEFAULT_CONFIG;

  const handleClick = () => {
    if (isUnread && onMarkRead) {
      onMarkRead(notification.id);
    }

    if (notification.expertSlug) {
      navigate(`/u/experts/${notification.expertSlug}`);
    }
  };

  return (
    <article
      role="article"
      onClick={handleClick}
      className={`
        relative rounded-xl border cursor-pointer
        transition-all duration-150 hover:shadow-md
        ${isUnread
          ? 'bg-white border-purple-200 shadow-sm shadow-purple-100/50'
          : 'bg-gray-50/70 border-gray-100 hover:bg-white'
        }
      `}
    >
      {/* Unread accent bar */}
      {isUnread && (
        <div className="absolute left-0 top-3 bottom-3 w-1 bg-purple-600 rounded-full" />
      )}

      <div className={`flex items-start gap-3.5 p-4 ${isUnread ? 'pl-5' : ''}`}>
        {/* Icon badge */}
        <div className={`
          flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center
          ${isUnread ? `${config.bg} ${config.color}` : 'bg-gray-100 text-gray-400'}
        `}>
          {config.icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className={`text-sm leading-snug ${
              isUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-500'
            }`}>
              {notification.title}
            </h3>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isUnread && (
                <span className="text-[10px] font-semibold uppercase tracking-wider text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded">
                  New
                </span>
              )}
              <time
                dateTime={notification.createdAt}
                className="text-xs text-gray-400 tabular-nums"
              >
                {getRelativeTime(notification.createdAt)}
              </time>
            </div>
          </div>

          {notification.body && (
            <p className={`mt-1 text-sm line-clamp-2 leading-relaxed ${
              isUnread ? 'text-gray-600' : 'text-gray-400'
            }`}>
              {notification.body}
            </p>
          )}

          {(notification.occursAt || notification.bookingId) && (
            <div className="mt-2 flex items-center gap-3">
              {notification.occursAt && (
                <span className={`text-xs ${isUnread ? 'text-gray-500' : 'text-gray-400'}`}>
                  {new Date(notification.occursAt).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </span>
              )}
              {notification.bookingId && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/u/bookings/${notification.bookingId}`);
                  }}
                  className={`text-xs font-medium ${
                    isUnread
                      ? 'text-purple-600 hover:text-purple-700'
                      : 'text-gray-400 hover:text-purple-600'
                  }`}
                >
                  View booking &rarr;
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default NotificationCard;
